import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, FileText, Mic, Sparkles, Calendar, Building2, User, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 20;

const EMPTY_IMG =
  "https://images.pexels.com/photos/31107362/pexels-photo-31107362.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const StatBadge = ({ has, icon: Icon, label, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium border ${
      has ? "border-foreground/30 text-foreground" : "border-border text-muted-foreground/50"
    }`}
  >
    <Icon size={12} strokeWidth={2} />
    {label}
  </span>
);

export default function MeetingHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("all");
  const departmentAccount = !isAdmin && Boolean(user?.department);
  const scope = isAdmin ? view : departmentAccount ? "all" : "mine";
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const params = { scope, page, page_size: PAGE_SIZE };
        if (q) params.q = q;
        if (department !== "all") params.department = department;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data } = await api.get("/meetings", { params, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (page > Math.max(1, data.total_pages)) {
          setPage(Math.max(1, data.total_pages));
          return;
        }
        setMeetings(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
        setLoading(false);
      } catch {
        if (!controller.signal.aborted) {
          setError("Unable to load meetings. Please try again.");
          setLoading(false);
        }
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, department, dateFrom, dateTo, scope, page, user?.id, retry]);

  useEffect(() => {
    const controller = new AbortController();
    setDepartments([]);
    api.get("/departments", { signal: controller.signal })
      .then((r) => { if (!controller.signal.aborted) setDepartments(r.data.departments); })
      .catch(() => {});
    return () => controller.abort();
  }, [user?.id, isAdmin]);

  const changeFilter = (setter, value) => {
    setter(value);
    setPage(1);
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 sm:mb-8 gap-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-medium mb-2 mono">Repository</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">Meeting History</h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              {isAdmin ? "Jelajahi rapat dari semua pengguna atau lihat rapat Anda sendiri." : departmentAccount ? `Lihat dan kelola rapat departemen ${user.department}.` : "Cari dan saring rapat yang Anda buat."}
            </p>
          </div>
          <Button
            onClick={() => navigate("/new")}
            data-testid="new-meeting-btn"
            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6 w-full sm:w-auto"
          >
            <Plus size={18} className="mr-2" /> New Meeting
          </Button>
        </div>

        {isAdmin && (
          <div role="group" aria-label="Meeting history scope" className="flex gap-2 mb-6">
            <Button variant={view === "all" ? "default" : "outline"} aria-pressed={view === "all"} onClick={() => changeFilter(setView, "all")} className="rounded-none" data-testid="all-meetings-tab">All Meetings</Button>
            <Button variant={view === "mine" ? "default" : "outline"} aria-pressed={view === "mine"} onClick={() => changeFilter(setView, "mine")} className="rounded-none" data-testid="my-meetings-tab">My Meetings</Button>
          </div>
        )}
        <section aria-label={scope === "all" ? "All Meetings" : "My Meetings"} data-testid="meeting-history-section">
        <h2 className="font-heading font-bold text-xl mb-4">{departmentAccount ? "Meeting Departemen" : scope === "all" ? "All Meetings" : "My Meetings"}</h2>

        {/* Filters */}
        <div className="border border-border bg-card p-4 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-input"
              placeholder="Search by title…"
              value={q}
              onChange={(e) => changeFilter(setQ, e.target.value)}
              className="rounded-none pl-9 focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          <div>
            <Select value={department} onValueChange={(value) => changeFilter(setDepartment, value)}>
              <SelectTrigger data-testid="filter-department" className="rounded-none">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Input
              type="date"
              data-testid="filter-date-from"
              value={dateFrom}
              onChange={(e) => changeFilter(setDateFrom, e.target.value)}
              className="rounded-none mono text-sm focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
          <div>
            <Input
              type="date"
              data-testid="filter-date-to"
              value={dateTo}
              onChange={(e) => changeFilter(setDateTo, e.target.value)}
              className="rounded-none mono text-sm focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-muted-foreground py-20 text-center" data-testid="meetings-loading">Loading meetings…</div>
        ) : error ? (
          <div role="alert" className="border border-border bg-card p-8 text-center">
            <p className="text-primary mb-4">{error}</p>
            <Button variant="outline" className="rounded-none" onClick={() => setRetry((value) => value + 1)}>Try again</Button>
          </div>
        ) : meetings.length === 0 ? (
          <div className="border border-border bg-card overflow-hidden" data-testid="empty-state">
            <div className="grid md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center">
                <h3 className="font-heading font-black text-2xl tracking-tight mb-2">No meetings found</h3>
                <p className="text-muted-foreground mb-6">
                  {q || department !== "all" || dateFrom || dateTo
                    ? "Try adjusting your filters."
                    : "Create your first meeting to start uploading recordings and minutes."}
                </p>
                <Button
                  onClick={() => navigate("/new")}
                  data-testid="empty-new-meeting-btn"
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground w-fit"
                >
                  <Plus size={18} className="mr-2" /> New Meeting
                </Button>
              </div>
              <div className="hidden md:block relative min-h-[280px] border-l border-border">
                <img src={EMPTY_IMG} alt="Meeting table" className="absolute inset-0 w-full h-full object-cover grayscale" />
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border bg-card" data-testid="meetings-list">
            {meetings.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate(`/meetings/${m.id}`)}
                data-testid={`meeting-row-${m.id}`}
                className="w-full text-left p-4 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-5 hover:bg-muted/50 transition-colors duration-150 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <h3 className="font-heading font-bold text-base sm:text-lg tracking-tight group-hover:text-primary transition-colors duration-150">
                      {m.title}
                    </h3>
                    {m.deletion_pending && <span className="text-xs text-primary border border-primary px-2 py-0.5">Penghapusan tertunda</span>}
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 size={12} /> {m.department}
                    </span>
                  </div>
                  <div className="mono text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Calendar size={12} /> {m.date}
                  </div>
                  {isAdmin && (
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <User size={12} /> {m.created_by_name || "Unknown creator"}
                      {m.created_by === user.id && <span>(You)</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <StatBadge has={m.recordings?.length > 0} icon={Mic} label={`${m.recordings?.length || 0} Rec`} testid={`row-rec-${m.id}`} />
                  <StatBadge has={!!m.minutes_current} icon={FileText} label="Minutes" testid={`row-min-${m.id}`} />
                  <StatBadge has={!!m.summary} icon={Sparkles} label="AI" testid={`row-ai-${m.id}`} />
                </div>
              </button>
            ))}
          </div>
        )}
        {!loading && !error && total > 0 && (
          <nav aria-label="Meeting pagination" className="flex flex-wrap items-center justify-between gap-4 mt-5" data-testid="meeting-pagination">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} meetings
            </p>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="rounded-none" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} data-testid="previous-page">
                <ChevronLeft size={16} className="mr-1" /> Previous
              </Button>
              <span className="text-sm mono">{page} / {Math.max(1, totalPages)}</span>
              <Button variant="outline" className="rounded-none" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} data-testid="next-page">
                Next <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </nav>
        )}
        </section>
      </div>
    </Layout>
  );
}
